import { useEffect, useMemo, useState } from 'react';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Box from '@mui/system/Box';
import './location-modal.scss';
import { GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import { SVGS } from '../../assets';
import moment from 'moment';
import { useGoogleAddress } from '../../customer';
import { CommonButton } from '../../components';
import { ClockInSkeleton } from '../clock-in/clock-in-skeleton';
import { getConfigValue } from '@zonofi/common/functions/get-config-value/get-config-value';

interface LocationModalProps {
  open: boolean;
  onClose: () => void;
  posCords: {
    latitude: number;
    longitude: number;
  };
  onLocationSelected?: (location: string) => void;
}

const GOOGLE_MAP_API_KEY = getConfigValue('GOOGLE_MAP_API_KEY');
export const LocationModal: React.FC<LocationModalProps> = ({
  open,
  onClose,
  posCords,
  onLocationSelected,
}) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAP_API_KEY,
  });

  const [currentLocation, setCurrentLocation] = useState('');
  const { getLocation } = useGoogleAddress();

  const fetchGoogleLocation = async () => {
    const location = await getLocation(posCords?.latitude, posCords?.longitude);
    setCurrentLocation(location);
  };

  useEffect(() => {
    if (posCords?.latitude && posCords?.longitude) {
      fetchGoogleLocation();
    }
  }, [posCords?.latitude, posCords?.longitude]);

  const locationItems = useMemo(
    () => [
      {
        key: 'location',
        title: 'Location',
        icon: SVGS.ZotokLoc,
        isBold: false,
        lines: [
          currentLocation || '',
          `Lat&Lon: ${posCords?.latitude}, ${posCords?.latitude}`,
        ],
      },
      {
        key: 'datetime',
        title: 'Date & Time',
        icon: SVGS.CalendarSchedule,
        isBold: true,
        lines: [moment().format('dddd, DD MMM YYYY - hh : mm : ss A')],
      },
    ],
    [posCords]
  );

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      PaperProps={{ className: 'location-modal__drawer' }}
    >
      <Box className="location-modal__container">
        {!currentLocation || !isLoaded ? (
          <ClockInSkeleton />
        ) : (
          <div className="location-modal__map">
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              zoom={15}
              center={{
                lat: Number(posCords?.latitude),
                lng: Number(posCords?.longitude),
              }}
              options={{
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
                zoomControl: false,
                disableDefaultUI: true,
              }}
            >
              <MarkerF
                position={{
                  lat: Number(posCords?.latitude),
                  lng: Number(posCords?.longitude),
                }}
                icon={{
                  url: SVGS.ZotokLoc,
                  scaledSize: new window.google.maps.Size(40, 40),
                }}
              />
            </GoogleMap>
          </div>
        )}

        {locationItems?.map((item) => (
          <div key={item.key} className="location-modal__info">
            <div className="location-modal__info-title font-semibold">
              {item.title}
            </div>

            <div className="location-modal__info-row">
              <img src={item.icon} className="location-modal__info-icon" />

              <div className="location-modal__info-lines">
                {item.lines.map((line, index) => (
                  <span
                    key={index}
                    className={`location-modal__info-text ${
                      item.isBold ? 'font-semibold' : 'font-regular'
                    }`}
                  >
                    {line}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}

        <CommonButton
          text={'Select location'}
          onPress={() => {
            onLocationSelected?.(currentLocation);
            onClose();
          }}
          buttonStyle={'location-modal__confirm-btn'}
          disabled={!currentLocation}
        />
      </Box>
    </SwipeableDrawer>
  );
};
